// Pointer / wheel / touch controls for the scene canvas. Pure event plumbing: the scene decides
// what a pan, a zoom or a click means. Returns a detach function.

const CLICK_SLOP = 5;       // px a pointer may move and still count as a click
const CLICK_MS = 650;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP = 26;

/**
 * @param {HTMLElement} el
 * @param {{
 *   onPan:(x0:number,y0:number,x1:number,y1:number)=>void,
 *   onPanEnd:()=>void,
 *   onZoom:(factor:number,x:number,y:number)=>void,
 *   onClick:(x:number,y:number)=>void,
 *   onDoubleClick:(x:number,y:number)=>void,
 *   onHover:(x:number,y:number)=>void,
 *   onLeave:()=>void,
 *   onInteract:(active:boolean)=>void,
 * }} h
 * @returns {() => void} detach
 */
export function attachControls(el, h) {
  const pointers = new Map();
  let drag = null;
  let pinch = null;
  let lastTap = null;

  const local = (e) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const pinchState = () => {
    const [a, b] = [...pointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };

  function down(e) {
    if (e.button !== undefined && e.button > 0 && e.pointerType === 'mouse') return;
    const p = local(e);
    pointers.set(e.pointerId, p);
    try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    if (pointers.size === 1) {
      drag = { x0: p.x, y0: p.y, x: p.x, y: p.y, t0: performance.now(), moved: false, type: e.pointerType };
      h.onInteract(true);
    } else if (pointers.size === 2) {
      pinch = pinchState();
      if (drag) drag.moved = true;
    }
  }

  function move(e) {
    const p = local(e);
    if (!pointers.has(e.pointerId)) {
      if (e.pointerType === 'mouse') h.onHover(p.x, p.y);
      return;
    }
    pointers.set(e.pointerId, p);
    if (pointers.size >= 2 && pinch) {
      const now = pinchState();
      if (now.dist > 0 && pinch.dist > 0) h.onZoom(pinch.dist / now.dist, now.x, now.y);
      h.onPan(pinch.x, pinch.y, now.x, now.y);
      pinch = now;
      return;
    }
    if (!drag) return;
    if (!drag.moved && Math.hypot(p.x - drag.x0, p.y - drag.y0) > CLICK_SLOP) drag.moved = true;
    if (drag.moved) {
      h.onPan(drag.x, drag.y, p.x, p.y);
      el.style.cursor = 'grabbing';
    }
    drag.x = p.x;
    drag.y = p.y;
  }

  function up(e) {
    if (!pointers.has(e.pointerId)) return;
    const p = local(e);
    pointers.delete(e.pointerId);
    try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (pointers.size === 1) {
      // Pinch ended with one finger left: continue as a drag from there.
      const [rest] = [...pointers.values()];
      pinch = null;
      drag = { x0: rest.x, y0: rest.y, x: rest.x, y: rest.y, t0: performance.now(), moved: true, type: 'touch' };
      return;
    }
    if (pointers.size > 0) return;
    pinch = null;
    const d = drag;
    drag = null;
    el.style.cursor = '';
    h.onInteract(false);
    if (!d) return;
    if (d.moved) {
      h.onPanEnd();
      return;
    }
    if (performance.now() - d.t0 > CLICK_MS || e.type === 'pointercancel') return;
    h.onClick(p.x, p.y);
    if (d.type !== 'mouse') {
      const now = performance.now();
      if (lastTap && now - lastTap.t < DOUBLE_TAP_MS && Math.hypot(p.x - lastTap.x, p.y - lastTap.y) < DOUBLE_TAP_SLOP) {
        h.onDoubleClick(p.x, p.y);
        lastTap = null;
      } else {
        lastTap = { t: now, x: p.x, y: p.y };
      }
    }
  }

  function dbl(e) {
    const p = local(e);
    h.onDoubleClick(p.x, p.y);
  }

  function wheel(e) {
    e.preventDefault();
    const p = local(e);
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 400;
    const k = e.ctrlKey ? 0.012 : 0.0016;
    const factor = Math.exp(Math.max(-60, Math.min(60, dy)) * k);
    h.onZoom(factor, p.x, p.y);
  }

  function leave(e) {
    if (e.pointerType === 'mouse' && !pointers.size) h.onLeave();
  }

  function preventGesture(e) {
    e.preventDefault();
  }

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);
  el.addEventListener('pointerleave', leave);
  el.addEventListener('dblclick', dbl);
  el.addEventListener('wheel', wheel, { passive: false });
  el.addEventListener('gesturestart', preventGesture);
  el.addEventListener('contextmenu', preventGesture);
  return () => {
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', up);
    el.removeEventListener('pointercancel', up);
    el.removeEventListener('pointerleave', leave);
    el.removeEventListener('dblclick', dbl);
    el.removeEventListener('wheel', wheel);
    el.removeEventListener('gesturestart', preventGesture);
    el.removeEventListener('contextmenu', preventGesture);
  };
}
